import Container from '@/components/@base/container';
import Timer from '@/components/timer';
import ozone_text_logo_light from '@/assets/logos/ozone-text-logo-light.svg';
import ozone_text_logo_dark from '@/assets/logos/ozone-text-logo-dark.svg';
import useCommonModalStore from '@/store/common-modal-store';
import CancelPayment from '@/components/cancel-payment';
import { useSearchParams } from 'react-router-dom';
import React, { useEffect, useState, useRef } from 'react';
import FailedPayment from '@/components/failed-payment';
import FontIcon from '@/components/@base/font-icon';
import useBottomSheetStore from '@/store/bottom-sheet-store';
import ActiveEditWallets from '@/components/active-edit-wallets-sheet';
import { Web } from 'sip.js';
import {
  useGetAdvertisement,
  useGetInvoices,
  usePostInitOTP,
  usePostRequestCall,
} from '@/services/hooks';
import cn from '@/lib/clsxm';
import Support from '@/components/support';
import { AnimatePresence } from 'framer-motion';
import { isDesktop } from '@/lib/helper';
import Spinner from '@/components/Spinner';
import { Text } from '@/components/@base/typography';
import { COLOR_ENUM, SIZE_ENUM } from '@/components/@base/@types';
import localization from '@/constant/localization';
import CheckBox from '@/components/@base/input/check-box/toggle';
import Button from '@/components/@base/button';

const Header = ({ showAds = false }: { showAds?: boolean }) => {
  const [showSupport, setShowSupport] = useState(false);
  const [userAgent, setUserAgent] = useState<Web.SimpleUser | null>(null);
  const [status, setStatus] = useState<string>('در حال برقراری ارتباط ...');
  const [callLoading, setCallLoading] = useState<boolean>(false);
  const [supportData, setSupportData] = useState<any>(null);

  // المان صوتی برای پخش صدای طرف مقابل
  const remoteAudioRef = useRef<HTMLAudioElement>(null);

  const { setShowBottomSheet } = useBottomSheetStore();
  const { data } = useGetInvoices();
  const { setShow } = useCommonModalStore();
  const [searchParams] = useSearchParams();
  const { data: advertise } = useGetAdvertisement(7, searchParams.get('pgc') as string);
  const { mutate: requestCall } = usePostRequestCall();

  const handleCancelPayment = () => {
    setShow(true, {
      center: true,
      dialogPanelClassName: 'px-0 pb-0 w-[85%] md:w-[380px]',
      Body: () => <CancelPayment />,
    });
  };

  const handleActivateEditeWallets = () => {
    isDesktop()
      ? setShow(true, {
          Body: () => <ActiveEditWallets />,
        })
      : setShowBottomSheet(true, {
          bgLighter: true,
          Body: () => <ActiveEditWallets />,
        });
  };

  const callToSupport = async () => {
    setCallLoading(true);
    setShow(false);

    // درخواست دسترسی میکروفون
    let micStream: MediaStream | null = null;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.error('Mic permission denied:', err);
      setStatus('دسترسی به میکروفون رد شد');
      setCallLoading(false);
      return;
    }

    requestCall(undefined, {
      onSuccess: async (res) => {
        setSupportData(res.data);
        setShowSupport(true);

        const { socketUrl, clientExtension, serverExtension, domain, password } = res.data;

        const options: Web.SimpleUserOptions = {
          aor: `sip:${clientExtension}@${domain}`,
          media: {
            remote: {
              audio: remoteAudioRef.current!,
            },
          },
          userAgentOptions: {
            displayName: 'Web Client',
            authorizationUsername: clientExtension,
            authorizationPassword: password,
            sessionDescriptionHandlerFactoryOptions: {
              peerConnectionConfiguration: {
                iceServers: [
                  { urls: 'stun:stun.l.google.com:19302' },
                  { urls: 'stun:stun1.l.google.com:19302' },
                ],
              },
            },
          },
        };

        const simpleUser = new Web.SimpleUser(socketUrl, options);

        simpleUser.delegate = {
          onCallAnswered: () => {
            setStatus('در تماس');
            // fix برای iOS Safari
            if (remoteAudioRef.current) {
              remoteAudioRef.current.play().catch(() => {});
            }
          },
          onCallHangup: () => {
            setStatus('پایان تماس');
            setTimeout(() => callSupport('close'), 2000);
          },
          onRegistered: () => setStatus('آنلاین'),
          onUnregistered: () => setStatus('آفلاین'),
          onServerConnect: () => setStatus('متصل'),
          onServerDisconnect: (error) => setStatus(`قطع ارتباط`),
        };

        try {
          await simpleUser.connect();
          await simpleUser.register();

          const targetURI = `sip:${serverExtension}@${domain}`;
          await simpleUser.call(targetURI);

          setUserAgent(simpleUser);
          setShowSupport(true);
        } catch (err) {
          console.error('Connection failed:', err);
          setStatus('خطا در ارتباط');
          // آزاد کردن میکروفون در صورت خطا
          micStream?.getTracks().forEach((track) => track.stop());
        }
      },
      onError: () => {
        setStatus('خطا در ارتباط');
        setCallLoading(false);
        micStream?.getTracks().forEach((track) => track.stop());
      },
    });
  };


  const callSupport = async (state: 'open' | 'close') => {
    if (state === 'open') {
      setShow(true, {
        center: true,
        Body: () => (
          <Container center className='flex-col p-4 pt-2'>
            <FontIcon
              icon='icon-headphone'
              className='mb-6 flex h-[56px] w-[56px] items-center justify-center rounded-full bg-blue-50 text-3xl text-blue'
            />
            <Text semiBold className='mb-2 text-xl text-grayLight-1000 dark:text-grayLight-0'>
              تماس اینترنتی با پشتیبانی
            </Text>
            <Text
              medium
              size={SIZE_ENUM.SM}
              className='mb-8 text-grayLight-850 dark:text-[#CCCCD1] text-center'
            >
              برای دریافت راهنمایی، می‌توانید بدون هزینه با پشتیبانی اوزون تماس بگیرید.
            </Text>
            <Button
              className='text-md w-full whitespace-nowrap rounded-full bg-blue hover:bg-blue px-8 text-white'
              style={{ height: '52px' }}
              size={SIZE_ENUM.XL}
              onClick={callToSupport}
              id='upg-choose-asset-selection'
            >
              تماس اینترنتی با پشتیبانی
            </Button>
            <Button
              className='text-md cursor-pointe mt-3 w-full whitespace-nowrap rounded-full bg-grayLight-100 px-8 text-grayLight-1000 hover:bg-grayLight-1000/10 dark:bg-grayDark-750 dark:text-white'
              style={{ height: '52px' }}
              size={SIZE_ENUM.XL}
              id='upg-continue-perchese-whithout-asset-selection'
              onClick={() => setShow(false)}
            >
              فعلا نه
            </Button>
          </Container>
        ),
      });

    } else if (state === 'close') {
      setCallLoading(false);
      setShowSupport(false);
      // قطع تماس، لغو ثبت نام و بستن اتصال
      if (userAgent) {
        userAgent.hangup().catch(console.error);
        userAgent.unregister().catch(console.error);
        userAgent.disconnect().catch(console.error);
        setUserAgent(null);
      }
      navigator.mediaDevices.getUserMedia({ audio: false }).catch(() => {});
      setCallLoading(false);
      setShowSupport(false);
      setStatus('قطع تماس');
    }
  };

  // بررسی وضعیت پرداخت ناموفق در URL
  useEffect(() => {
    if (searchParams.has('IPGStatus') && searchParams.get('IPGStatus') === 'Failed') {
      setShow(true, {
        center: true,
        dialogPanelClassName: 'px-0 pb-0 w-[280px]',
        Body: () => <FailedPayment />,
      });
    }
  }, [searchParams, setShow]);

  // پاکسازی اتصال SIP هنگام از بین رفتن کامپوننت (Unmount)
  useEffect(() => {
    return () => {
      if (userAgent) {
        userAgent.unregister().catch(console.error);
        userAgent.disconnect().catch(console.error);
      }
    };
  }, [userAgent]);

  return (
    <Container
      center
      className={cn(
        'w-full flex-col gap-3 border-b border-grayLight-50 px-3 pb-3 pt-4 dark:border-grayDark-750',
        advertise && advertise?.ads?.length > 0 && 'border-none pb-1',
      )}
    >
      <AnimatePresence mode='wait'>
        {showSupport && (
          <Support
            supportData={supportData}
            status={status}
            cancelCall={() => callSupport('close')}
          />
        )}
      </AnimatePresence>
      <Container center className='relative w-full justify-between'>
        <Container center className='gap-2'>
          <FontIcon
            icon='icon-slider-horizontal'
            onClick={handleActivateEditeWallets}
            className='flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-grayLight-50 text-lg dark:bg-grayDark-750 dark:text-white'
          />

          {callLoading ? (
            <Container className='flex h-10 w-10 items-center justify-center rounded-full bg-grayLight-50 text-lg dark:bg-grayDark-750 dark:text-white'>
              <Container className='loading-dot' />
            </Container>
          ) : (
            <FontIcon
              icon='icon-call-righ'
              onClick={() => callSupport('open')}
              className='flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-grayLight-50 text-lg dark:bg-grayDark-750 dark:text-white'
            />
          )}
        </Container>

        <img
          src={ozone_text_logo_light}
          alt='ozone card logo'
          className={cn(
            'absolute right-1/2 translate-x-1/2 dark:hidden',
            data?.isEpg && 'hidden',
          )}
        />
        <img
          src={ozone_text_logo_dark}
          alt='ozone card logo'
          className={cn(
            'absolute right-1/2 hidden translate-x-1/2 dark:block',
            data?.isEpg && 'dark:hidden',
          )}
        />
        <Container
          center
          className='h-10 rounded-full bg-grayLight-50 p-[3px] dark:bg-grayDark-750'
        >
          <Timer />
          <Container
            center
            className='h-12 w-[40px] cursor-pointer '
            onClick={handleCancelPayment}
          >
            <FontIcon icon='icon-cross' className='text-2xl dark:text-white' />
          </Container>
        </Container>
      </Container>
      {advertise &&
        showAds &&
        advertise?.ads?.length > 0 &&
        advertise?.ads[0]?.banner &&
        advertise?.ads[0]?.banner?.length > 0 && (
          <Container className='relative'>
            <img
              src={advertise?.ads[0]?.banner}
              alt='ads'
              className={cn('min-h-10 w-full rounded-lg')}
            />
          </Container>
        )}

      {/* المان صوتی برای خروجی صدا */}
      <audio ref={remoteAudioRef} autoPlay className='hidden' />
    </Container>
  );
};

export default Header;
